import type {
  Point,
  SketchPointerEvent,
  SketchPointerPhase,
  SketchPointerType,
} from "../../../../../types/stage-types.js";
import type { SketchViewportAPI } from "./viewport.js";
import { logger } from "../../../../../utils/logger.ts";

export interface SketchModuleConfig {
  readonly viewport: SketchViewportAPI;
  readonly surface?: HTMLElement | SVGElement;
  readonly logPointerEvents?: boolean;
}

export interface SketchModuleAPI {
  readonly activate: () => void;
  readonly deactivate: () => void;
  readonly destroy: () => void;
  readonly isActive: () => boolean;
  readonly onPointerEvent: (
    listener: (event: SketchPointerEvent) => void
  ) => () => void;
  readonly getActivePointerType: () => SketchPointerType | null;
  readonly processPointerEvent: (
    phase: SketchPointerPhase,
    event: PointerEvent,
    options?: { preventDefault?: boolean; usePointerCapture?: boolean }
  ) => void;
  /** Clear an orphaned pointer from internal tracking (for cleanup during rapid strokes) */
  readonly clearOrphanedPointer: (pointerId: number) => void;
}

const normalizePointerType = (value: string): SketchPointerType => {
  if (value === "mouse" || value === "pen" || value === "touch") {
    return value;
  }
  return "unknown";
};

const PALM_SIZE_PX = 45;
const PALM_BLOCK_MS = 200;

const isLargeTouchContact = (event: PointerEvent): boolean => {
  const width = typeof event.width === "number" ? event.width : 0;
  const height = typeof event.height === "number" ? event.height : 0;
  return width > PALM_SIZE_PX && height > PALM_SIZE_PX;
};

/**
 * Creates the sketch interaction module.
 * Handles pointer capture and translates web pointer events into sketch-space coordinates.
 */
export const createSketchModule = (
  config: SketchModuleConfig
): SketchModuleAPI => {
  const surface = config.surface ?? config.viewport.element;
  const subscribers = new Set<(event: SketchPointerEvent) => void>();
  const activePointers = new Map<number, SketchPointerType>();
  const activeTouchPointers = new Set<number>();
  const pendingTouchDowns = new Map<number, PointerEvent>();
  const pendingTouchTimers = new Map<number, number>();

  let activePointerType: SketchPointerType | null = null;
  let isActive = false;
  let suppressTouch = false;
  let penActiveAt = 0;

  const getCoalescedPositions = (
    event: PointerEvent
  ): Point[] | undefined => {
    if (typeof event.getCoalescedEvents !== "function") {
      return undefined;
    }

    const coalesced = event.getCoalescedEvents();
    if (!coalesced || coalesced.length === 0) {
      return undefined;
    }

    const positions = coalesced.map((entry) =>
      config.viewport.screenToSketch({
        x: entry.clientX,
        y: entry.clientY,
      })
    );

    return positions.length > 0 ? positions : undefined;
  };

  const emit = (
    phase: SketchPointerPhase,
    event: PointerEvent
  ): SketchPointerEvent => {
    const pointerType =
      activePointers.get(event.pointerId) ??
      normalizePointerType(event.pointerType);

    const position = config.viewport.screenToSketch({
      x: event.clientX,
      y: event.clientY,
    });

    const coalescedPositions = getCoalescedPositions(event);
    const payload: SketchPointerEvent = {
      phase,
      position,
      coalescedPositions,
      pointerId: event.pointerId,
      pointerType,
      pressure: typeof event.pressure === "number" ? event.pressure : 0,
      isPrimary: event.isPrimary,
      originalEvent: event,
    };

    if (config.logPointerEvents !== false) {
      logger.debug("sketch", "✏️ Pointer event", {
        phase,
        pointerType,
        position,
        pressure: payload.pressure,
        pointerId: payload.pointerId,
      });
    }

    subscribers.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        logger.warn("sketch", "Pointer listener threw", error);
      }
    });

    return payload;
  };

  const setPointerCapture = (pointerId: number): void => {
    const target = surface as unknown as {
      setPointerCapture?: (pointerId: number) => void;
    };
    try {
      target.setPointerCapture?.(pointerId);
    } catch {
      // Ignore capture errors (browser differences)
    }
  };

  const releasePointerCapture = (pointerId: number): void => {
    const target = surface as unknown as {
      releasePointerCapture?: (pointerId: number) => void;
    };
    try {
      target.releasePointerCapture?.(pointerId);
    } catch {
      // Ignore release errors (browser differences)
    }
  };

  const releasePointer = (event: PointerEvent): void => {
    if (!activePointers.has(event.pointerId)) return;

    releasePointerCapture(event.pointerId);

    activePointers.delete(event.pointerId);
    if (activePointers.size === 0) {
      activePointerType = null;
    }
  };

  const processPointerEvent = (
    phase: SketchPointerPhase,
    event: PointerEvent,
    options: { preventDefault?: boolean; usePointerCapture?: boolean } = {}
  ): void => {
    if (!isActive) {
      return;
    }

    const shouldPreventDefault = options.preventDefault !== false;
    const shouldCapture = options.usePointerCapture !== false;

    const pointerEvent = event;
    const normalizedPointerType = normalizePointerType(pointerEvent.pointerType);

    const clearPendingTouch = (pointerId: number): void => {
      const timer = pendingTouchTimers.get(pointerId);
      if (typeof timer === "number") {
        clearTimeout(timer);
      }
      pendingTouchTimers.delete(pointerId);
      pendingTouchDowns.delete(pointerId);
    };

    const promotePendingTouch = (pointerId: number): boolean => {
      const pending = pendingTouchDowns.get(pointerId);
      if (!pending) return false;
      if (suppressTouch || activeTouchPointers.size !== 1) {
        clearPendingTouch(pointerId);
        return false;
      }
      clearPendingTouch(pointerId);
      activePointerType = normalizePointerType(pending.pointerType);
      activePointers.set(pointerId, activePointerType);
      emit("down", pending);
      return true;
    };

    if (normalizedPointerType === "touch") {
      if (phase === "down") {
        activeTouchPointers.add(pointerEvent.pointerId);
        if (activeTouchPointers.size >= 2) {
          suppressTouch = true;
          pendingTouchDowns.forEach((_value, pid) => clearPendingTouch(pid));
          return;
        }
        if (!suppressTouch) {
          pendingTouchDowns.set(pointerEvent.pointerId, pointerEvent);
          const timerId = window.setTimeout(() => {
            promotePendingTouch(pointerEvent.pointerId);
          }, 60);
          pendingTouchTimers.set(pointerEvent.pointerId, timerId);
        }
        return;
      }

      if (phase === "move") {
        if (suppressTouch) return;
        if (pendingTouchDowns.has(pointerEvent.pointerId)) {
          if (!promotePendingTouch(pointerEvent.pointerId)) return;
        }
      }

      if (phase === "up" || phase === "cancel") {
        if (pendingTouchDowns.has(pointerEvent.pointerId)) {
          clearPendingTouch(pointerEvent.pointerId);
        }
        activeTouchPointers.delete(pointerEvent.pointerId);
        if (activeTouchPointers.size < 2) {
          suppressTouch = false;
        }
        if (suppressTouch) return;
      }
    }

    if (
      normalizedPointerType === "touch" &&
      activePointerType === "pen" &&
      activePointers.size > 0
    ) {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - penActiveAt < PALM_BLOCK_MS) {
        return;
      }
      if (isLargeTouchContact(pointerEvent)) {
        return;
      }
    }

    switch (phase) {
      case "down": {
        // ALWAYS prevent default on down to ensure we get all subsequent events
        pointerEvent.preventDefault();

        // For pen input, clear any stale pointers before starting new stroke
        // This handles edge cases where up/cancel events were missed
        if (pointerEvent.pointerType === "pen" && activePointers.size > 0) {
          const stalePointers = [...activePointers.keys()].filter(
            (id) => id !== pointerEvent.pointerId
          );
          if (stalePointers.length > 0) {
            stalePointers.forEach((id) => {
              releasePointerCapture(id);
              activePointers.delete(id);
            });
          }
        }

        activePointerType = normalizedPointerType;
        activePointers.set(pointerEvent.pointerId, activePointerType);
        if (activePointerType === "pen") {
          penActiveAt =
            typeof performance !== "undefined" ? performance.now() : Date.now();
        }
        if (shouldCapture) {
          setPointerCapture(pointerEvent.pointerId);
        }

        emit("down", pointerEvent);
        break;
      }
      case "move": {
        if (!activePointers.has(pointerEvent.pointerId)) {
          return;
        }
        // ALWAYS prevent default on move during active drawing
        pointerEvent.preventDefault();
        emit("move", pointerEvent);
        break;
      }
      case "up": {
        // ALWAYS prevent default on up to ensure clean state for next stroke
        pointerEvent.preventDefault();
        
        if (!activePointers.has(pointerEvent.pointerId)) {
          return;
        }
        emit("up", pointerEvent);
        releasePointer(pointerEvent);
        break;
      }
      case "cancel": {
        // ALWAYS prevent default on cancel to stop browser from interfering
        // with subsequent pointer events (fixes rapid stroke issue on iPad)
        pointerEvent.preventDefault();
        
        if (!activePointers.has(pointerEvent.pointerId)) {
          return;
        }
        emit("cancel", pointerEvent);
        releasePointer(pointerEvent);
        break;
      }
      default:
        break;
    }
  };

  const handlePointerDown: EventListener = (event) => {
    processPointerEvent("down", event as PointerEvent);
  };

  const handlePointerMove: EventListener = (event) => {
    processPointerEvent("move", event as PointerEvent);
  };

  const handlePointerUp: EventListener = (event) => {
    processPointerEvent("up", event as PointerEvent);
  };

  const handlePointerCancel: EventListener = (event) => {
    processPointerEvent("cancel", event as PointerEvent);
  };

  /**
   * Touch event handlers to prevent Safari gesture recognition.
   * Safari on iPad uses touch events internally even for Apple Pencil,
   * and can swallow rapid successive touches if it thinks they're gestures.
   */
  const handleTouchStart: EventListener = (event) => {
    const touchEvent = event as TouchEvent;
    const hasPencil = hasPencilTouch(touchEvent.touches);
    suppressTouch = touchEvent.touches.length > 1;

    // If any touch is Apple Pencil, prevent default to keep pointer events flowing.
    if (hasPencil) {
      event.preventDefault();
    }
  };

  const handleTouchMove: EventListener = (event) => {
    const touchEvent = event as TouchEvent;
    suppressTouch = touchEvent.touches.length > 1;
    if (activePointers.size > 0 || hasPencilTouch(touchEvent.touches)) {
      event.preventDefault();
    }
  };

  const handleTouchEnd: EventListener = (event) => {
    const touchEvent = event as TouchEvent;
    suppressTouch = touchEvent.touches.length > 1;
    // Always prevent default on touch end to avoid Safari gesture completion.
    event.preventDefault();
  };

  const handleGestureEvent: EventListener = (event) => {
    event.preventDefault();
  };

  /**
   * Touch event helper to detect Apple Pencil touches.
   * Used by Stage when centralizing touch handling.
   */
  const isLikelyPencilTouch = (touch: Touch): boolean => {
    // Apple Pencil has very precise touch with small radius
    // Regular finger has radiusX/radiusY around 20-30
    // Apple Pencil shows as touch type "stylus" on newer Safari
    return (
      (touch.radiusX !== undefined && touch.radiusX < 10) ||
      (touch as any).touchType === "stylus"
    );
  };

  /**
   * Check if any touch in the touch list is likely a pencil touch.
   * This handles the case where the user is holding a modifier button
   * with their finger while drawing with the pencil (multi-touch).
   */
  const hasPencilTouch = (touches: TouchList): boolean => {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      if (touch && isLikelyPencilTouch(touch)) {
        return true;
      }
    }
    return false;
  };

  // Event listener options for non-passive handling (required for preventDefault)
  const nonPassiveOptions: AddEventListenerOptions = { passive: false };
  
  // Store original styles to restore on deactivate
  let originalTouchAction: string | null = null;
  let originalUserSelect: string | null = null;

  const activate = (): void => {
    if (isActive) return;
    isActive = true;

    // Ensure the surface has touch-action: none to prevent Safari gesture recognition
    // This is critical for receiving rapid Apple Pencil strokes
    const surfaceStyle = (surface as HTMLElement).style;
    if (surfaceStyle) {
      originalTouchAction = surfaceStyle.touchAction;
      originalUserSelect = surfaceStyle.userSelect;
      surfaceStyle.touchAction = "none";
      surfaceStyle.userSelect = "none";
      (surfaceStyle as any).webkitUserSelect = "none";
      (surfaceStyle as any).webkitTouchCallout = "none";
    }

    // Add pointer event listeners with { passive: false } to ensure
    // preventDefault() works correctly on iPad Safari
    surface.addEventListener("pointerdown", handlePointerDown, nonPassiveOptions);
    surface.addEventListener("pointermove", handlePointerMove, nonPassiveOptions);
    surface.addEventListener("pointerup", handlePointerUp, nonPassiveOptions);
    surface.addEventListener("pointercancel", handlePointerCancel, nonPassiveOptions);
    surface.addEventListener("pointerleave", handlePointerCancel, nonPassiveOptions);

    // iPad Safari requires touch/gesture listeners on the sketch surface to
    // prevent pointer event dropouts during Apple Pencil strokes.
    surface.addEventListener("touchstart", handleTouchStart, nonPassiveOptions);
    surface.addEventListener("touchmove", handleTouchMove, nonPassiveOptions);
    surface.addEventListener("touchend", handleTouchEnd, nonPassiveOptions);
    surface.addEventListener("gesturestart", handleGestureEvent, nonPassiveOptions);
    surface.addEventListener("gesturechange", handleGestureEvent, nonPassiveOptions);
    surface.addEventListener("gestureend", handleGestureEvent, nonPassiveOptions);
  };

  const deactivate = (): void => {
    if (!isActive) return;
    isActive = false;

    // Remove all event listeners
    surface.removeEventListener("pointerdown", handlePointerDown, nonPassiveOptions);
    surface.removeEventListener("pointermove", handlePointerMove, nonPassiveOptions);
    surface.removeEventListener("pointerup", handlePointerUp, nonPassiveOptions);
    surface.removeEventListener("pointercancel", handlePointerCancel, nonPassiveOptions);
    surface.removeEventListener("pointerleave", handlePointerCancel, nonPassiveOptions);
    surface.removeEventListener("touchstart", handleTouchStart, nonPassiveOptions);
    surface.removeEventListener("touchmove", handleTouchMove, nonPassiveOptions);
    surface.removeEventListener("touchend", handleTouchEnd, nonPassiveOptions);
    surface.removeEventListener("gesturestart", handleGestureEvent, nonPassiveOptions);
    surface.removeEventListener("gesturechange", handleGestureEvent, nonPassiveOptions);
    surface.removeEventListener("gestureend", handleGestureEvent, nonPassiveOptions);
    // Restore original styles
    const surfaceStyle = (surface as HTMLElement).style;
    if (surfaceStyle) {
      if (originalTouchAction !== null) {
        surfaceStyle.touchAction = originalTouchAction;
      }
      if (originalUserSelect !== null) {
        surfaceStyle.userSelect = originalUserSelect;
      }
    }

    activePointers.clear();
    activePointerType = null;
  };

  const destroy = (): void => {
    deactivate();
    subscribers.clear();
  };

  const onPointerEvent = (
    listener: (event: SketchPointerEvent) => void
  ): (() => void) => {
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  };

  const getActivePointerType = (): SketchPointerType | null =>
    activePointerType;

  /**
   * Clear an orphaned pointer from internal tracking.
   * Used when cleaning up during rapid stroke succession where
   * the "up" event may have been lost or delayed.
   */
  const clearOrphanedPointer = (pointerId: number): void => {
    if (activePointers.has(pointerId)) {
      releasePointerCapture(pointerId);
      activePointers.delete(pointerId);
      if (activePointers.size === 0) {
        activePointerType = null;
      }
    }
  };

  return {
    activate,
    deactivate,
    destroy,
    isActive: () => isActive,
    onPointerEvent,
    getActivePointerType,
    processPointerEvent,
    clearOrphanedPointer,
  };
};
